// DriverLinkBadge.jsx
// Polls `/api/admin/bookings/{id}/link-events` and renders a one-line status
// indicator for the assigned driver's SMS link clicks. Dispatcher-only.
//
// Visual states:
//   ✓ Opened link 9:31 PM · 📅 Added to calendar 9:32 PM   (green)
//   ✓ Opened link 9:31 PM                                  (green, no cal click)
//   ⚠ Not opened yet · sent ~5 min ago                      (amber after 5 min)
//   🛑 Not opened yet · sent 12 min ago — call the driver   (red after 10 min)
//   …                                                      (loading state)

import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric", minute: "2-digit", timeZone: "America/Chicago",
    });
  } catch { return ""; }
};

const minutesAgo = (iso) => {
  if (!iso) return null;
  try {
    return Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  } catch { return null; }
};

export default function DriverLinkBadge({ booking }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!booking?.booking_id || !booking?.assigned_driver_id) return;
    let cancelled = false;
    let timer;

    const load = async () => {
      try {
        const r = await api.get(`/admin/bookings/${booking.booking_id}/link-events`);
        if (!cancelled) { setData(r.data); setLoaded(true); }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    // Poll every 30s while the drawer is open so the indicator stays fresh
    timer = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [booking?.booking_id, booking?.assigned_driver_id]);

  if (!booking?.assigned_driver_id) return null;
  if (!loaded) return (
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-1.5" data-testid="driver-link-badge">
      Checking SMS link…
    </div>
  );

  const tripAt = data?.first_trip_opened_at;
  const calAt = data?.first_calendar_opened_at;
  // Best-effort "sent at" — fall back to assigned_at, accepted_at, or created_at
  const sentRef = booking.assigned_at || booking.driver_assigned_at || booking.updated_at || booking.created_at;
  const mins = minutesAgo(sentRef);

  if (tripAt) {
    return (
      <div
        className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 mt-1.5 flex flex-wrap gap-x-3 gap-y-1"
        data-testid="driver-link-badge"
        title="Driver opened the SMS trip link (auto-tracked)"
      >
        <span data-testid="link-trip-opened">✓ Opened link {fmtTime(tripAt)}</span>
        {calAt && <span className="text-[#D4AF37]" data-testid="link-cal-opened">📅 Added to calendar {fmtTime(calAt)}</span>}
      </div>
    );
  }

  // Not opened yet — escalate by elapsed minutes
  if (mins !== null && mins >= 10) {
    return (
      <div
        className="text-[10px] uppercase tracking-[0.18em] text-red-400 mt-1.5 font-medium"
        data-testid="driver-link-badge"
        title="Driver has not opened the SMS link in 10+ minutes — recommend a phone call"
      >
        🛑 Not opened yet · sent {mins} min ago — call the driver
      </div>
    );
  }
  if (mins !== null && mins >= 5) {
    return (
      <div
        className="text-[10px] uppercase tracking-[0.18em] text-amber-400 mt-1.5"
        data-testid="driver-link-badge"
        title="Driver hasn't opened the SMS link yet (5+ min)"
      >
        ⚠ Not opened yet · sent {mins} min ago
      </div>
    );
  }
  return (
    <div
      className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-1.5"
      data-testid="driver-link-badge"
    >
      … Awaiting driver to open SMS link{mins !== null ? ` (sent ${mins} min ago)` : ""}
    </div>
  );
}
